export interface RuntimeConfig {
  /** Backend or APIM base URL, without a trailing slash. Empty string = same origin. */
  apiUrl: string;
  /**
   * Optional APIM product key. Injected at deploy time into config.json, never compiled
   * into the bundle. Empty = do not send Ocp-Apim-Subscription-Key (local proxy / gateway).
   */
  ocpApimSubscriptionKey: string;
}

/** Thrown when config.json is absent, malformed, or not substituted by the pipeline. */
export class RuntimeConfigError extends Error {
  override readonly name = 'RuntimeConfigError';
}

/**
 * Runtime configuration, loaded from /config.json before the app bootstraps.
 *
 * The APIM (or backend) URL and optional subscription key are deliberately NOT compiled
 * into the bundle. The pipeline writes them into config.json from `apiBaseUrl` and
 * `apimSubscriptionKey`.
 *
 * There is no build-time fallback: if config.json cannot be read the app refuses to start
 * rather than silently calling whichever backend happened to be baked in.
 */
let config: RuntimeConfig | null = null;

function asOptionalString(value: unknown): string {
  if (value == null) return '';
  if (typeof value !== 'string') {
    throw new RuntimeConfigError('config.json has a non-string APIM field.');
  }
  return value.trim();
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const url = new URL('config.json', document.baseURI).toString();

  let response: Response;
  try {
    response = await fetch(url, { cache: 'no-store' });
  } catch (err) {
    throw new RuntimeConfigError(
      `Could not fetch ${url}. ${err instanceof Error ? err.message : String(err)}`
    );
  }

  if (!response.ok) {
    throw new RuntimeConfigError(`${url} returned HTTP ${response.status}.`);
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    throw new RuntimeConfigError(
      `${url} did not return JSON. The file is probably missing from the deployment and ` +
        `the SPA fallback rule served index.html instead.`
    );
  }

  const raw = parsed as {
    apiUrl?: unknown;
    ocpApimSubscriptionKey?: unknown;
    subscriptionKey?: unknown;
  } | null;

  if (typeof raw?.apiUrl !== 'string') {
    throw new RuntimeConfigError(`${url} has no string "apiUrl" property.`);
  }

  const apiUrl = raw.apiUrl.trim().replace(/\/+$/, '');
  if (apiUrl.startsWith('__') && apiUrl.endsWith('__')) {
    throw new RuntimeConfigError(
      `${url} still contains the placeholder "${apiUrl}". The deployment did not ` +
        `substitute the apiBaseUrl pipeline variable.`
    );
  }

  let ocpApimSubscriptionKey = asOptionalString(
    raw.ocpApimSubscriptionKey ?? raw.subscriptionKey
  );
  if (
    ocpApimSubscriptionKey.startsWith('__') &&
    ocpApimSubscriptionKey.endsWith('__')
  ) {
    throw new RuntimeConfigError(
      `${url} still contains an unsubstituted APIM subscription-key placeholder. ` +
        `Set the apimSubscriptionKey pipeline secret.`
    );
  }

  config = { apiUrl, ocpApimSubscriptionKey };
  return config;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (config === null) {
    throw new RuntimeConfigError('Runtime configuration was not loaded before use.');
  }
  return config;
}

/** Backend / APIM base URL. Callers append the path, e.g. `${getApiUrl()}/api/chat`. */
export function getApiUrl(): string {
  return getRuntimeConfig().apiUrl;
}

/** APIM subscription key, or empty when not used. */
export function getApimSubscriptionKey(): string {
  return getRuntimeConfig().ocpApimSubscriptionKey;
}

/** Test-only: clear the singleton so the next loadRuntimeConfig() fetches again. */
export function resetRuntimeConfig(): void {
  config = null;
}

/** Test-only: inject a config without fetching. */
export function setRuntimeConfigForTests(value: RuntimeConfig): void {
  config = value;
}
